import { c, msgid, ngettext } from 'ttag';

import { DataScope, MarketStatus } from '../../graphql/__generated__/schema';

/**
 * Display shared by the list and the sheet. It lives here rather than in each
 * screen because a date shown two ways, or a perimeter named two ways, reads as
 * two different facts.
 */

/** A dash, never an empty cell: a blank reads as a loading row. */
export const EMPTY = '-';

/** The API returns ISO strings; the mockup shows plain dates. */
export const formatDate = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleDateString() : EMPTY;

export const formatDateTime = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleString() : EMPTY;

/**
 * What a membership may see. `ALL` is **not** « every activity »: it is the absence of a
 * personal restriction, so the count of the account's activities would be a number nobody
 * restricted. The two are said differently on purpose.
 *
 * **Attention, IL COMPTAIT DES MARCHES JUSQU'À PCG-305**, et son compteur venait de
 * `visibleMarkets`. Ce tableau n'est plus le périmètre et restera vide : le laisser aurait
 * fait écrire « restreint à 0 marchés » sur un périmètre parfaitement rempli. Il compte
 * désormais les ACTIVITÉS, besoins et prestations confondus, parce que c'est ce qu'un
 * périmètre retient et que l'opérateur n'a pas à faire la somme lui-même.
 */
export const dataScopeLabel = (scope: DataScope | null | undefined, restrictedCount: number): string => {
    if (scope === DataScope.Restricted) {
        return ngettext(
            msgid`Restricted to ${restrictedCount} activity`,
            `Restricted to ${restrictedCount} activities`,
            restrictedCount
        );
    }
    return c('User perimeter').t`The whole account`;
};

/**
 * The two-factor authentication of a person: what the POOL holds, and when they first
 * complied (PCG-303).
 *
 * **THREE STATES AND NOT TWO, and the third is the reason this helper exists.**
 *
 * - `state` null: the pool did not answer. Absent from the pool, a failed request, or
 *   inside-api unavailable. « Aucune » here would report a non-compliance about somebody
 *   the pool may never have heard of, so the screen says so instead;
 * - `factors` empty: that IS an answer, nothing is registered;
 * - factors present: the pool's own vocabulary, rendered as such.
 *
 * **The pool's names are not translated into a shorter list of ours.** `SOFTWARE_TOKEN_MFA`
 * and `EMAIL_OTP` get a French label because an operator reads them every day; anything
 * else stays visible as itself rather than being folded into « autre », which is how a
 * fourth value the pool starts returning would disappear from the screen.
 *
 * **« Aucun facteur » does NOT mean « not compliant ».** The requirement may never have
 * been set on this person's account, in which case there is nothing to comply with. The
 * sentence says what the pool holds and draws no conclusion.
 */
export const mfaLabel = (
    state: { factors: string[]; preferred?: string | null } | null | undefined,
    enabledAt: string | null | undefined
): string => {
    if (!state) {
        return c('User mfa').t`State not read from the pool`;
    }
    if (state.factors.length === 0) {
        return c('User mfa').t`No factor registered`;
    }

    const named = state.factors.map((factor) => {
        if (factor === 'SOFTWARE_TOKEN_MFA') {
            return c('User mfa').t`Authenticator app`;
        }
        if (factor === 'EMAIL_OTP') {
            return c('User mfa').t`Code sent by email`;
        }
        if (factor === 'SMS_MFA') {
            return c('User mfa').t`Code sent by SMS`;
        }
        // Rendered as itself: folding an unknown value into « autre » is how a factor
        // the pool starts returning disappears from this screen.
        return factor;
    });
    const factors = named.join(', ');

    if (!enabledAt) {
        return factors;
    }
    // L'evenement se dit, il ne se separe pas par un signe : la date porte un fait
    // (le moment de la mise en conformite) qu'une ponctuation ne dirait pas.
    const since = formatDate(enabledAt);

    return c('User mfa').t`${factors} since ${since}`;
};

/**
 * How a market's end is named, or null while it has not ended.
 *
 * Attention, `status` and not `isClosed`, and the two are not a choice between equals: the
 * view computes them from the same expression, so `isClosed` is exactly
 * `status !== ACTIVE`: showing both would put two words on screen for one fact.
 * The one that survives is the one that says **why** it ended, because « annulé »
 * and « échu » do not call for the same second look from an administrator about to
 * grant them.
 *
 * A finished market is labelled, never hidden and never unticked: it stays visible
 * in the portal, and dropping it would cut the person off from their own past
 * business.
 */
export const marketEndLabel = (status: MarketStatus | null | undefined): string | null => {
    switch (status) {
        case MarketStatus.Cancelled:
            return c('Market status').t`Cancelled`;
        case MarketStatus.Expired:
            return c('Market status').t`Expired`;
        case MarketStatus.Completed:
            return c('Market status').t`Finished`;
        case MarketStatus.Settled:
            return c('Market status').t`Settled`;
        // ACTIVE, and a null the API sends when it did not compute one: neither is
        // an ending, and « en cours » stamped on every running market would be noise
        // on the majority to annotate the minority.
        default:
            return null;
    }
};
